import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  PointerSensor,
  DragOverlay,
  pointerWithin,
  closestCenter,
  MeasuringStrategy,
  useDroppable,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/ui/card";
import { RankingOption } from "./pollTypes";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

interface PollRankingMinimumFormProps {
  options: RankingOption[];
  order: string[];
  onOrderChange: (order: string[]) => void;
  minimumRequired: number;
}

const sid = (value: string | number) => String(value);

const SortableOption = ({ option, containerId }: { option: RankingOption; containerId: string }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sid(option.id!),
    data: { containerId },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className="p-3 flex items-center gap-3">
      <div className="w-12 h-12 rounded-md overflow-hidden bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground">
        {option.image_url ? (
          <img src={option.image_url} alt={option.label} className="w-full h-full object-cover" loading="lazy" width={48} height={48} />
        ) : (
          <span>{option.label?.charAt(0).toUpperCase() ?? "?"}</span>
        )}
      </div>
      <div className="flex-1 space-y-1">
        <p className="font-medium leading-none">{option.label}</p>
        {option.description && <p className="text-sm text-muted-foreground">{option.description}</p>}
      </div>
      <button
        {...attributes}
        {...listeners}
        className={cn(
          "p-2 rounded-md border bg-muted/40 hover:bg-muted flex items-center justify-center ml-auto",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring",
        )}
        aria-label={`Drag to reorder ${option.label}`}
        type="button"
      >
        <GripVertical className="w-4 h-4" />
      </button>
    </Card>
  );
};

const OptionPreview = ({ option }: { option: RankingOption }) => (
  <Card className="p-3 flex items-center gap-3 shadow-lg border-2 border-primary">
    <div className="w-12 h-12 rounded-md overflow-hidden bg-muted flex items-center justify-center text-sm font-semibold text-muted-foreground">
      {option.image_url ? (
        <img src={option.image_url} alt={option.label} className="w-full h-full object-cover" loading="lazy" width={48} height={48} />
      ) : (
        <span>{option.label?.charAt(0).toUpperCase() ?? "?"}</span>
      )}
    </div>
    <div className="flex-1 space-y-1">
      <p className="font-medium leading-none">{option.label}</p>
      {option.description && <p className="text-sm text-muted-foreground">{option.description}</p>}
    </div>
    <div className="p-2 rounded-md border bg-muted/40">
      <GripVertical className="w-4 h-4" />
    </div>
  </Card>
);

const PlaceholderDropZone = ({ 
  containerId, 
  minimumRequired 
}: { 
  containerId: string; 
  minimumRequired?: number;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${containerId}-placeholder`,
    data: { type: "placeholder", containerId },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex items-center justify-center h-20 rounded-lg border-2 border-dashed transition-colors",
        isOver ? "border-primary bg-primary/10" : "border-muted-foreground/30"
      )}
    >
      <p className="text-sm text-muted-foreground">
        {containerId === "ranked" 
          ? `Drop here to rank (at least ${minimumRequired} options)` 
          : "Drop here to unrank"}
      </p>
    </div>
  );
};

export const PollRankingMinimumForm = ({
  options,
  order,
  onOrderChange,
  minimumRequired,
}: PollRankingMinimumFormProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const rankedIds = useMemo(() => {
    const seen = new Set<string>();
    return order.filter((id) => {
      if (seen.has(id)) return false;
      if (!options.some((opt) => sid(opt.id!) === id)) return false;
      seen.add(id);
      return true;
    });
  }, [options, order]);

  useEffect(() => {
    if (rankedIds.length !== order.length) {
      onOrderChange(rankedIds);
    }
  }, [onOrderChange, order.length, rankedIds]);

  const rankedOptions = rankedIds
    .map((id) => options.find((option) => sid(option.id!) === id))
    .filter((option): option is RankingOption => Boolean(option));

  const unrankedOptions = options.filter((option) => !rankedIds.includes(sid(option.id!)));

  const { setNodeRef: setRankedRef, isOver: isOverRanked } = useDroppable({
    id: "ranked-container",
    data: { type: "container", containerId: "ranked" },
  });
  const { setNodeRef: setUnrankedRef, isOver: isOverUnranked } = useDroppable({
    id: "unranked-container",
    data: { type: "container", containerId: "unranked" },
  });

  const activeOption = activeId ? options.find((opt) => sid(opt.id!) === activeId) : null;

  const findContainer = useCallback(
    (id: string): "ranked" | "unranked" | null => {
      if (id === "ranked" || id === "ranked-container" || id === "ranked-placeholder") return "ranked";
      if (id === "unranked" || id === "unranked-container" || id === "unranked-placeholder") return "unranked";
      if (rankedIds.includes(id)) return "ranked";
      if (unrankedOptions.some((opt) => sid(opt.id!) === id)) return "unranked";
      return null;
    },
    [rankedIds, unrankedOptions],
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const collisionDetectionStrategy = (args: Parameters<typeof pointerWithin>[0]) => {
    const pointerHits = pointerWithin(args);
    if (pointerHits.length > 0) return pointerHits;
    return closestCenter(args);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const getContainer = (id: string, data: any): "ranked" | "unranked" | null => {
      // If the "over" target is the container droppable or placeholder
      if (id === "ranked-container" || id === "ranked" || id === "ranked-placeholder") return "ranked";
      if (id === "unranked-container" || id === "unranked" || id === "unranked-placeholder") return "unranked";

      // If droppable data indicates a container or placeholder
      if (data?.type === "container" || data?.type === "placeholder") return data.containerId;

      // If it's a sortable item, dnd-kit stores the SortableContext id here:
      const sortableContainer = data?.sortable?.containerId;
      if (sortableContainer === "ranked" || sortableContainer === "unranked") return sortableContainer;

      // Fallback: compute from current state
      return findContainer(id);
    };

    const activeContainer = getContainer(activeId, active.data.current);
    const overContainer = getContainer(overId, over.data.current);

    if (!activeContainer || !overContainer) return;

    // Moving between containers
    if (activeContainer !== overContainer) {
      if (activeContainer === "unranked" && overContainer === "ranked") {
        const overIndex = rankedIds.indexOf(overId);
        if (overIndex === -1) {
          onOrderChange([...rankedIds, activeId]);
        } else {
          const newRanked = [...rankedIds];
          newRanked.splice(overIndex, 0, activeId);
          onOrderChange(newRanked);
        }
      }

      if (activeContainer === "ranked" && overContainer === "unranked") {
        onOrderChange(rankedIds.filter((id) => id !== activeId));
      }

      return;
    }

    // Reordering within ranked container
    if (activeContainer === "ranked" && activeId !== overId) {
      const oldIndex = rankedIds.indexOf(activeId);
      const newIndex = rankedIds.indexOf(overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        onOrderChange(arrayMove(rankedIds, oldIndex, newIndex));
      }
    }
  };

  const requiredCount = Math.min(minimumRequired, options.length);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div data-no-drag="true" className="space-y-6 touch-none">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Ranked options</p>
            <span className="text-xs text-muted-foreground">Min {requiredCount}</span>
          </div>
          <div
            ref={setRankedRef}
            className={cn(
              "rounded-lg border-2 border-border p-3 min-h-[120px] bg-background transition-colors",
              isOverRanked && "border-success bg-success/10",
            )}
          >
            <SortableContext
              id="ranked"
              items={rankedOptions.map((option) => sid(option.id!))}
              strategy={verticalListSortingStrategy}
            >
              {rankedOptions.length === 0 ? (
                <PlaceholderDropZone containerId="ranked" minimumRequired={requiredCount} />
              ) : (
                <div className="space-y-2">
                  {rankedOptions.map((option) => (
                    <SortableOption key={option.id} option={option} containerId="ranked" />
                  ))}
                </div>
              )}
            </SortableContext>
          </div>
        </div>

        <div className="border-t border-dashed" />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Unranked options</p>
            <span className="text-xs text-muted-foreground">Drag above to rank</span>
          </div>
          <div
            ref={setUnrankedRef}
            className={cn(
              "rounded-lg border-2 border-border p-3 min-h-[100px] bg-muted/40 transition-colors",
              isOverUnranked && "border-info bg-info/10",
            )}
          >
            <SortableContext
              id="unranked"
              items={unrankedOptions.map((option) => sid(option.id!))}
              strategy={verticalListSortingStrategy}
            >
              {unrankedOptions.length === 0 ? (
                <PlaceholderDropZone containerId="unranked" />
              ) : (
                <div className="space-y-2">
                  {unrankedOptions.map((option) => (
                    <SortableOption key={option.id} option={option} containerId="unranked" />
                  ))}
                </div>
              )}
            </SortableContext>
          </div>
        </div>
      </div>

      {createPortal(
        <DragOverlay dropAnimation={null} zIndex={1000} className="pointer-events-none">
          {activeOption && <OptionPreview option={activeOption} />}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};
